import { useState } from "react";
import {
  AgendaListItem,
  BlueprintModal,
  AgendaRemixModal,
  RenameAgendaModal,
} from "@components";
import { Skeleton, Stack, useToken } from "@chakra-ui/react";
import { useRemixAgendaMutation } from "@store/api/agenda";
import RemixLimitModal from "../Modals/RemixLimitModal";

const AgendaList = ({
  agendas,
  emptyStateComponent,
  isPastAgenda,
  loadingAgendas,
  setAgendaId,
  setAgendaDeleteModalOpen,
  setAgendaDuplicateModalOpen,
  navigateToReports = false, // New prop to control navigation behavior
}) => {
  const [blueprintModalOpen, setBlueprintModalOpen] = useState(false);
  const [selectedBlueprint, setSelectedBlueprint] = useState(null);
  const [remixModalOpen, setRemixModalOpen] = useState(false);
  const [selectedAgenda, setSelectedAgenda] = useState(null);
  const [isRemixLimitModalOpen, setIsRemixLimitModalOpen] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [selectedAgendaForRename, setSelectedAgendaForRename] = useState(null);
  const [gray100, gray300] = useToken("colors", ["gray.100", "gray.300"]);

  const [remixAgenda, { isLoading: isRemixing }] = useRemixAgendaMutation();

  const handleBlueprintOpen = (blueprint) => {
    console.log("Opening blueprint modal with:", blueprint);
    setSelectedBlueprint(blueprint);
    setBlueprintModalOpen(true);
  };

  const handleBlueprintClose = () => {
    setBlueprintModalOpen(false);
    setSelectedBlueprint(null);
  };

  const handleRemixAgenda = (agenda) => {
    console.log("Opening remix modal for agenda:", agenda);
    setSelectedAgenda(agenda);
    setRemixModalOpen(true);
  };

  const handleRemixModalClose = () => {
    setRemixModalOpen(false);
    setSelectedAgenda(null);
  };

  const handleRemixComplete = async (remixData) => {
    try {
      console.log("Remixing agenda with data:", remixData);

      const payload = {
        agendaId: selectedAgenda._id,
        body: {
          hostRefinements: remixData.hostRefinements,
          tone: remixData.tone,
          demographics: remixData.demographics,
          audience: remixData.audience,
          topics: remixData.topics,
          goals: remixData.goals,
          keepActivityTypes: remixData.keepActivityTypes,
        },
      };

      await remixAgenda(payload).unwrap();

      console.log("Agenda remixed successfully!");
      handleRemixModalClose();
    } catch (error) {
      console.error("Failed to remix agenda:", error);

      // Check if it's a limit exceeded error (403 status)
      if (
        error?.status === 403 ||
        error?.data?.message?.includes("limit exceeded")
      ) {
        handleRemixModalClose();
        setIsRemixLimitModalOpen(true);
        return;
      }

      // TODO: Show error toast/notification for other errors
    }
  };

  const handleRenameAgenda = (agenda) => {
    setSelectedAgendaForRename(agenda);
    setRenameModalOpen(true);
  };

  const handleRenameModalClose = () => {
    setRenameModalOpen(false);
    setSelectedAgendaForRename(null);
  };

  if (loadingAgendas) {
    const ITEMS = [1, 2, 3, 4];

    return (
      <Stack spacing="12px" my="1">
        {ITEMS.map((s) => (
          <Skeleton key={s} height="72px" borderRadius="8px" />
        ))}
      </Stack>
    );
  }

  return agendas?.length > 0 ? (
    <Stack
      id="host-onboarding-step-1"
      overflowY="auto"
      sx={{
        scrollbarColor: `${gray300} ${gray100}`,
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": {
          width: "5px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "gray.100",
          borderRadius: "5px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "gray.300",
          borderRadius: "5px",
        },
      }}
    >
      {agendas.map((agenda, index) => {
        const { _id, blueprint } = agenda;

        // Extract meetingType from blueprint data (when available)
        // TODO: Update when blueprint data is sent from backend
        const meetingType = blueprint?.meetingType || null;

        return (
          <AgendaListItem
            id={_id}
            key={`agenda-list-item-${_id}`}
            index={index}
            agendaLength={agendas.length}
            {...agenda}
            isPastAgenda={isPastAgenda}
            setAgendaId={setAgendaId}
            setAgendaDeleteModalOpen={setAgendaDeleteModalOpen}
            setAgendaDuplicateModalOpen={setAgendaDuplicateModalOpen}
            meetingType={meetingType}
            blueprint={blueprint}
            onBlueprintOpen={handleBlueprintOpen}
            onRemix={handleRemixAgenda}
            onRename={handleRenameAgenda}
            isRemixed={agenda.remixed || false}
            navigateToReports={navigateToReports}
          />
        );
      })}

      {/* Blueprint Modal */}
      <BlueprintModal
        isOpen={blueprintModalOpen}
        onClose={handleBlueprintClose}
        blueprint={selectedBlueprint}
        onRemix={handleRemixAgenda}
        isPastAgenda={isPastAgenda}
      />

      {/* Agenda Remix Modal */}
      <AgendaRemixModal
        isOpen={remixModalOpen}
        onClose={handleRemixModalClose}
        agenda={selectedAgenda}
        onRemixComplete={handleRemixComplete}
        isLoading={isRemixing}
      />

      {/* Remix Limit Modal */}
      <RemixLimitModal
        isOpen={isRemixLimitModalOpen}
        onClose={() => setIsRemixLimitModalOpen(false)}
      />

      {/* Rename Agenda Modal */}
      <RenameAgendaModal
        isOpen={renameModalOpen}
        onClose={handleRenameModalClose}
        agendaId={selectedAgendaForRename?._id}
        currentTitle={selectedAgendaForRename?.title}
      />
    </Stack>
  ) : (
    <>
      {emptyStateComponent}

      {/* Blueprint Modal */}
      <BlueprintModal
        isOpen={blueprintModalOpen}
        onClose={handleBlueprintClose}
        blueprint={selectedBlueprint}
        onRemix={handleRemixAgenda}
      />

      {/* Agenda Remix Modal */}
      <AgendaRemixModal
        isOpen={remixModalOpen}
        onClose={handleRemixModalClose}
        agenda={selectedAgenda}
        onRemixComplete={handleRemixComplete}
        isLoading={isRemixing}
      />

      {/* Remix Limit Modal */}
      <RemixLimitModal
        isOpen={isRemixLimitModalOpen}
        onClose={() => setIsRemixLimitModalOpen(false)}
      />
    </>
  );
};

export default AgendaList;
